import { useEffect, useMemo, useState } from "react";
import CustomizeDialog from "components/Car/CustomizeDialog";
import { useFormik } from "formik";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { GradientButtonOutline } from "components/GradientButton";
import CustomizeDatePicker from "components/DatePicker";
import useApi from "_api/index";
import nationalities from "data/nationalities_th.json";
import { useCarBrands } from "contexts/CarBrandsContext";
import { getBrandName } from "utils/helpers/brandCar";
import * as yup from "yup";

const Attachments = [
  { id: 1, label: "ขอโอนและรับโอน" },
  { id: 2, label: "ซื้อขายรถ" },
];

const ModalPowerOfAttorney = ({
  car,
  isOpen,
  onClose,
  customer = {},
  order = {},
  type = "buy",
  document = null,
  onSuccess,
}) => {
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  useEffect(() => {
    console.log({ order, document, type });
    if (isOpen && !document) {
      // Fetch or prepare data needed for the Power of Attorney form
      formik.setValues({
        ...formik.values,
        grantor_name: customer?.customer_name || "",
        grantor_phone: customer?.customer_phonenumber || "",
        grantor_address: customer?.customer_address || "",
      });
    }
    if (isOpen && document && document.document_id) {
      fetchDocument();
    }
    if (!isOpen) {
      formik.resetForm();
      setSelectedAttachments([]);
    }
  }, [isOpen, document, car]);

  const fetchDocument = async () => {
    try {
      let response = null;
      if (type == "buy") {
        response = await useApi.buy_car.getPowerOfAttorneyBuyCarByID(
          document.document_id
        );
      } else if (type == "sell_car") {
        response = await useApi.ready_car.getPowerOfAttorneyReadyByID(
          document.document_id
        );
      } else if (type == "pawn_car") {
        response = await useApi.pawn_car.getPowerOfAttorneyPawnByID(
          document.document_id
        );
      }
      if (response?.success || response?.isStatus) {
        const data = response.data;
        const formattedValues = {
          ...data,
        };
        // Remove file fields from form values and set selected attachments
        const files = [];
        for (let i = 1; i <= 6; i++) {
          const key = `document_file_${i}`;
          const file = data?.[key];
          if (file != null) {
            files.push(file);
          }
          delete formattedValues[key];
        }
        setSelectedAttachments(files);
        formik.setValues(formattedValues);
      }
    } catch (error) {
      console.error("Error fetching Power of Attorney document:", error);
    }
  };

  const { carBrands } = useCarBrands();

  const brandName = useMemo(() => {
    if (!car || !car.car_brand_id) return "ยี่ห้อรถยนต์";
    // const brand = carBrands.find((b) => b.id === car.car_brand_id);
    return getBrandName(car.car_brand_id, carBrands);
  }, [car, carBrands]);

  const generateValidationSchema = () => {
    return yup.object({
      // Define validation schema here if needed
      grantor_name: yup.string().required("กรุณากรอกชื่อผู้มอบอำนาจ"),
      attorney_name: yup.string().required("กรุณากรอกชื่อผู้รับมอบอำนาจ"),
      grantor_age: yup.number().required("กรุณากรอกอายุผู้มอบอำนาจ"),
      attorney_age: yup.number().required("กรุณากรอกอายุผู้รับมอบอำนาจ"),
      grantor_nationality: yup
        .string()
        .required("กรุณาเลือกสัญชาติผู้มอบอำนาจ"),
      attorney_nationality: yup
        .string()
        .required("กรุณาเลือกสัญชาติผู้รับมอบอำนาจ"),
      grantor_phone: yup.string().required("กรุณากรอกเบอร์โทรศัพท์ผู้มอบอำนาจ"),
      attorney_phone: yup
        .string()
        .required("กรุณากรอกเบอร์โทรศัพท์ผู้รับมอบอำนาจ"),
      grantor_address: yup.string().required("กรุณากรอกที่อยู่ผู้มอบอำนาจ"),
      attorney_address: yup.string().required("กรุณากรอกที่อยู่ผู้รับมอบอำนาจ"),
    });
  };

  const formik = useFormik({
    initialValues: {
      // Define initial form values here
      created_date: new Date(),
      grantor_name: "",
      grantor_age: "",
      grantor_nationality: "ไทย",
      grantor_phone: "",
      grantor_address: "",
      attorney_name: "",
      attorney_age: "",
      attorney_nationality: "ไทย",
      attorney_phone: "",
      attorney_address: "",
    },
    validationSchema: generateValidationSchema(), // Add validation schema if needed
    onSubmit: (values) => {
      console.log("Form submitted with values:", values);
      // Handle form submission
      const formData = {
        ...values,
        document_file: selectedAttachments,
      };
      if (type == "buy") {
        handleSubmitPowerOfAttorney(formData);
      } else if (type == "sell_car") {
        handleSellPowerOfAttorney(formData);
      } else if (type == "pawn_car") {
        handlePawnPowerOfAttorney(formData);
      }
    },
  });

  const handleSubmitPowerOfAttorney = async (values) => {
    try {
      const data = {
        ...values,
      };

      if (document && document.document_id) {
        const response = await useApi.buy_car.updatePowerOfAttorneyBuy(
          document.document_id,
          data
        );
        if (response.success) {
          onClose();
          onSuccess && onSuccess();
        } else {
          // Handle errors from the API
          console.error(
            "Failed to update Power of Attorney:",
            response.message
          );
        }
      } else {
        const response = await useApi.buy_car.createPowerOfAttorneyBuyCar(
          order.order_id,
          data
        );
        if (response.success) {
          onClose();
          onSuccess && onSuccess();
        } else {
          // Handle errors from the API
          console.error(
            "Failed to create Power of Attorney:",
            response.message
          );
        }
      }
    } catch (error) {
      console.error("Error creating Power of Attorney:", error);
    }
  };

  const handleSellPowerOfAttorney = async (values) => {
    try {
      const data = {
        ...values,
      };
      let response = null;
      if (document && document.document_id) {
        response = await useApi.ready_car.updatePowerOfAttorneyReady(
          document.document_id,
          data
        );
      } else {
        response = await useApi.ready_car.createPowerOfAttorneyReady(
          order.order_id,
          data
        );
      }
      if (response.success) {
        // Handle successful form submission
        console.log("Power of Attorney created successfully:", response.data);
        onSuccess && onSuccess();
        onClose();
      } else {
        // Handle errors from the API
        console.error("Failed to create Power of Attorney:", response.message);
      }
    } catch (error) {
      console.error("Error creating Power of Attorney:", error);
    }
  };

  const handlePawnPowerOfAttorney = async (values) => {
    try {
      const data = {
        ...values,
      };
      console.log("Pawn Power of Attorney data:", document);
      let response = null;
      if (document && document.document_id) {
        response = await useApi.pawn_car.updatePowerOfAttorneyPawn(
          document.document_id,
          data
        );
      } else {
        response = await useApi.pawn_car.createPowerOfAttorneyPawn(
          order.order_id,
          data
        );
      }
      if (response.success) {
        // Handle successful form submission
        console.log("Power of Attorney created successfully:", response.data);
        onSuccess && onSuccess();
        onClose();
      } else {
        // Handle errors from the API
        console.error("Failed to create Power of Attorney:", response.message);
      }
    } catch (error) {
      console.error("Error creating Power of Attorney:", error);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <CustomizeDialog
        open={isOpen}
        onClose={onClose}
        title="หนังสือมอบอำนาจ"
        fullWidth
        maxWidth="md"
        onSave={() => {
          formik.submitForm();
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            gap: 1,
            width: "100%",
            "& > button": {
              height: "40px",
              cursor: "default",
              pointerEvents: "none",
              background: "rgba(0, 187, 159, 0.2)",
              "&:hover": {
                background: "transparent",
              },
              boxShadow: "none",
            },
          }}
        >
          <GradientButtonOutline>
            {car ? car.car_type : "รถยนต์"}
          </GradientButtonOutline>
          <GradientButtonOutline>
            {car ? brandName : "ยี่ห้อรถยนต์"}
          </GradientButtonOutline>
          <GradientButtonOutline>
            {car ? car.license_plate + " " + car.license_province : "ทะเบียนรถ"}
          </GradientButtonOutline>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            หนังสือมอบอำนาจ
          </Typography>
          <Grid container spacing={2} px={1}>
            <Grid item xs={12}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={2}>
                  <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                    วันที่
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <CustomizeDatePicker
                    value={formik.values["created_date"]}
                    onChange={(newValue) => {
                      formik.setFieldValue("create_date", newValue);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        height: "40px",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
            ข้อมูลผู้มอบอำนาจ
          </Typography>
          <Grid container spacing={2} px={1} alignItems="center">
            <Grid item xs={12}>
              <FieldRow
                field={{
                  name: "grantor_name",
                  label: "ชื่อผู้ขาย",
                  type: "text",
                  placeholder: "กรุณากรอกชื่อผู้ขาย",
                }}
                formik={formik}
              />
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                อายุ
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                type="number"
                name="grantor_age"
                placeholder="กรุณากรอกอายุ"
                value={formik.values["grantor_age"]}
                onChange={(e) => {
                  // limit max min
                  if (e.target.value === "") {
                    formik.setFieldValue("grantor_age", "");
                    return;
                  }
                  let value = parseInt(e.target.value);
                  if (value > 150) value = 150;
                  formik.setFieldValue("grantor_age", value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    height: "40px",
                  },
                }}
                InputProps={{ inputProps: { min: 18, max: 150 } }}
                onKeyPress={(e) => {
                  // number only
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                helperText={
                  formik.touched.grantor_age && formik.errors.grantor_age
                }
                error={
                  formik.touched.grantor_age &&
                  Boolean(formik.errors.grantor_age)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <Typography
                variant="body1"
                sx={{ whiteSpace: "nowrap", textAlign: "center" }}
              >
                สัญชาติ
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                options={nationalities}
                getOptionLabel={(option) => option.nationality}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="กรุณากรอกสัญชาติ"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        height: "40px",
                      },
                    }}
                    error={
                      formik.touched.grantor_nationality &&
                      Boolean(formik.errors.grantor_nationality)
                    }
                    helperText={
                      formik.touched.grantor_nationality &&
                      formik.errors.grantor_nationality
                    }
                  />
                )}
                value={
                  nationalities.find(
                    (n) => n.nationality === formik.values.grantor_nationality
                  ) || null
                }
                onChange={(event, newValue) => {
                  formik.setFieldValue(
                    "grantor_nationality",
                    newValue.nationality
                  );
                }}
                disableClearable
              />
            </Grid>

            <Grid item xs={12}>
              <FieldRow
                field={{
                  name: "grantor_phone",
                  label: "เบอร์โทรศัพท์",
                  type: "text",
                  placeholder: "กรุณากรอกเบอร์โทรศัพท์",
                }}
                formik={formik}
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FieldRow
                field={{
                  name: "grantor_address",
                  label: "ที่อยู่",
                  type: "text",
                  placeholder: "กรุณากรอกที่อยู่",
                }}
                formik={formik}
              />
            </Grid>
          </Grid>

          <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
            ข้อมูลผู้รับมอบอำนาจ
          </Typography>
          <Grid container spacing={2} px={1} alignItems="center">
            <Grid item xs={12}>
              <FieldRow
                field={{
                  name: "attorney_name",
                  label: "ชื่อผู้ซื้อ",
                  type: "text",
                  placeholder: "กรุณากรอกชื่อผู้ซื้อ",
                }}
                formik={formik}
              />
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                อายุ
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                type="number"
                name="attorney_age"
                placeholder="กรุณากรอกอายุ"
                value={formik.values["attorney_age"]}
                onChange={(e) => {
                  // limit max min
                  if (e.target.value === "") {
                    formik.setFieldValue("attorney_age", "");
                    return;
                  }
                  let value = parseInt(e.target.value);
                  if (value > 150) value = 150;
                  formik.setFieldValue("attorney_age", value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    height: "40px",
                  },
                }}
                InputProps={{ inputProps: { min: 18, max: 150 } }}
                onKeyPress={(e) => {
                  // number only
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                helperText={
                  formik.touched.attorney_age && formik.errors.attorney_age
                }
                error={
                  formik.touched.attorney_age &&
                  Boolean(formik.errors.attorney_age)
                }
              />
            </Grid>
            <Grid item xs={2}>
              <Typography
                variant="body1"
                sx={{ whiteSpace: "nowrap", textAlign: "center" }}
              >
                สัญชาติ
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                options={nationalities}
                getOptionLabel={(option) => option.nationality}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="กรุณากรอกสัญชาติ"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        height: "40px",
                      },
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.attorney_nationality &&
                      Boolean(formik.errors.attorney_nationality)
                    }
                    helperText={
                      formik.touched.attorney_nationality &&
                      formik.errors.attorney_nationality
                    }
                  />
                )}
                value={
                  nationalities.find(
                    (n) => n.nationality === formik.values.attorney_nationality
                  ) || null
                }
                onChange={(event, newValue) => {
                  formik.setFieldValue(
                    "attorney_nationality",
                    newValue.nationality
                  );
                }}
                disableClearable
              />
            </Grid>
            <Grid item xs={12}>
              <FieldRow
                field={{
                  name: "attorney_phone",
                  label: "เบอร์โทรศัพท์",
                  type: "text",
                  placeholder: "กรุณากรอกเบอร์โทรศัพท์",
                }}
                formik={formik}
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldRow
                field={{
                  name: "attorney_address",
                  label: "ที่อยู่",
                  type: "text",
                  placeholder: "กรุณากรอกที่อยู่",
                }}
                formik={formik}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            การดำเนินการ
          </Typography>
          <Box sx={{ ml: 4 }}>
            <FormGroup row>
              {Attachments.map((attachment) => (
                <FormControlLabel
                  key={attachment.id}
                  control={
                    <Checkbox
                      checked={selectedAttachments.includes(attachment.label)}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (isChecked) {
                          setSelectedAttachments((prev) => [
                            ...prev,
                            attachment.label,
                          ]);
                        } else {
                          setSelectedAttachments((prev) =>
                            prev.filter((item) => item !== attachment.label)
                          );
                        }
                      }}
                      name={attachment.label}
                    />
                  }
                  label={attachment.label}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      </CustomizeDialog>
    </form>
  );
};

const FieldRow = ({ field, formik, ...props }) => (
  <Grid container alignItems="center" spacing={1}>
    <Grid item xs={2}>
      <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
        {field.label}
      </Typography>
    </Grid>
    <Grid item xs={10}>
      {field.type === "text" || field.type === "number" ? (
        <TextField
          {...props}
          fullWidth
          variant="outlined"
          type={field.type}
          name={field.name}
          placeholder={field.placeholder}
          value={formik.values[field.name]}
          onChange={formik.handleChange}
          onKeyPress={(e) => {
            if (field.name.includes("phone")) {
              const charCode = e.which ? e.which : e.keyCode;
              if (charCode < 48 || charCode > 57) {
                e.preventDefault();
              }
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": { bgcolor: "white", height: "40px" },
          }}
          onBlur={formik.handleBlur}
          error={
            formik.touched[field.name] && Boolean(formik.errors[field.name])
          }
          helperText={formik.touched[field.name] && formik.errors[field.name]}
          disabled={props.disabled}
          {...props}
        />
      ) : (
        <Autocomplete
          options={nationalities}
          getOptionLabel={(option) => option.nationality}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={field.placeholder}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                  height: "40px",
                },
              }}
            />
          )}
          disableClearable
          {...props}
        />
      )}
    </Grid>
  </Grid>
);

export default ModalPowerOfAttorney;
